import { useEffect } from 'react';
import { getDeviceToken as getToken } from '../utils/deviceToken';

/**
 * Hook to ensure device token is always available in localStorage
 * This hook runs on every component mount to verify the token exists
 */
export const useDeviceToken = () => {
    useEffect(() => {
        const checkDeviceToken = () => {
            // getToken will auto-generate if missing
            const token = getToken();

            if (!token) {
                console.warn('⚠️ Device token missing from localStorage');
                return false;
            }

            return true;
        };

        // Check on mount
        checkDeviceToken();

        // Optional: Set up an interval to check periodically
        const interval = setInterval(() => {
            const hasToken = checkDeviceToken();
            if (!hasToken) {
                console.error('🚨 Device token was removed from localStorage!');
            }
        }, 5000); // Check every 5 seconds

        return () => clearInterval(interval);
    }, []);
};

/**
 * Utility function to safely get device token
 */
export const getDeviceToken = (): string | null => {
    try {
        return localStorage.getItem('device_token');
    } catch (error) {
        console.error('Error reading device token from localStorage:', error);
        return null;
    }
};

/**
 * Utility function to safely set device token
 */
export const setDeviceToken = (token: string): boolean => {
    try {
        localStorage.setItem('device_token', token);
        console.log('✅ Device token saved to localStorage');
        return true;
    } catch (error) {
        console.error('Error saving device token to localStorage:', error);
        return false;
    }
};

/**
 * Utility function to safely remove device token
 */
export const removeDeviceToken = (): void => {
    try {
        localStorage.removeItem('device_token');
        console.log('🗑️ Device token removed from localStorage');
    } catch (error) {
        console.error('Error removing device token from localStorage:', error);
    }
};
